/**
 * UX helper for pending AI reclaim messaging.
 * The timer text is just a visual nice-to-have for Office users, so they can see when the next batch will run.
 */
import { AutoReclaimEvaluationStatus } from '@trustoo/types/entity/reclaim'

const AUTO_RECLAIM_BATCH_HOURS = [10, 13, 16]
const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, {
    numeric: 'always',
})

const getNextBatchInfo = (
    now: Date = new Date(),
): { minutesUntilNextBatch: number; nextBatchHour: number } => {
    const currentMinutes = now.getHours() * 60 + now.getMinutes()
    const batchMinutes = AUTO_RECLAIM_BATCH_HOURS.map((hour) => hour * 60)

    let nextBatchMinutes = batchMinutes.find((minutes) => minutes > currentMinutes)
    if (nextBatchMinutes === undefined) {
        nextBatchMinutes = batchMinutes[0] + 24 * 60
    }

    const minutesUntilNextBatch = Math.max(1, nextBatchMinutes - currentMinutes)
    const nextBatchHour = Math.floor((nextBatchMinutes % (24 * 60)) / 60)

    return { minutesUntilNextBatch, nextBatchHour }
}

const getNextBatchTimerText = (): string => {
    const { minutesUntilNextBatch } = getNextBatchInfo()

    if (minutesUntilNextBatch < 60) {
        return relativeTimeFormatter.format(minutesUntilNextBatch, 'minute')
    }

    return relativeTimeFormatter.format(Math.ceil(minutesUntilNextBatch / 60), 'hour')
}

/**
 * Get the label for a pending AI reclaim based on its status
 */
export const getPendingAiLabel = (status: AutoReclaimEvaluationStatus | null): string => {
    if (status === 'approved') {
        return 'Pending AI processing (approved)'
    }
    if (status === 'rejected') {
        return 'Pending AI processing (rejected)'
    }
    return 'Pending AI processing (waiting for decision)'
}

/**
 * Get the hover text for a pending AI reclaim based on its status
 */
export const getPendingAiHoverText = (_status: AutoReclaimEvaluationStatus | null): string =>
    getNextBatchTimerText()
